import _ from 'lodash'
import moment from 'moment'
import { BloodUnit, DateRange, MaterialCapacityScale, ComponentExpireStart } from '@/lib/enums'
import utils from '@/lib/utils'
import rules from '@/lib/rules'
import store from '@/store'
import Notify from 'quasar/src/plugins/Notify.js';

export const mergeFlags = utils.mergeFlags
export const matchFlags = utils.matchFlags

export function getDateRangeObject (dateRange, format, now) {
  const formatFunc = format instanceof Function
    ? format
    : (format
      ? function (val) { return utils.formatDateTime(val, format) }
      : utils.formatDateTime)
  switch (dateRange) {
    case DateRange.Day: // 当天
      return {
        start: formatFunc(moment(now).startOf('day')),
        end: formatFunc(moment(now).endOf('day'))
      }
    case DateRange.Month: // 本月
      return {
        start: formatFunc(moment(now).startOf('month')),
        end: formatFunc(moment(now).endOf('month'))
      }
    case DateRange.LastMonth: // 上月
      return {
        start: formatFunc(moment(now).add(-1, 'month').startOf('month')),
        end: formatFunc(moment(now).add(-1, 'month').endOf('month'))
      }
    case DateRange.Year: // 今年
      return {
        start: formatFunc(moment(now).startOf('year')),
        end: formatFunc(moment(now).endOf('year'))
      }
    default:
      return {
        start: null,
        end: null
      }
  }
}

export function getDateRangeValue ({ start, end }) {
  if (start && end) {
    start = utils.formatDate(start)
    end = utils.formatDate(end)
    for (const item of DateRange.items) {
      const dateRangeObject = getDateRangeObject(item.value, utils.formatDate)
      if (dateRangeObject.start === start && dateRangeObject.end === end) {
        return item.value
      }
    }
  }
}

const modelComparers = {
  visit (item1, item2) {
    return utils.compareDateTime(item2.visitTime ?? item2.createTime, item1.visitTime ?? item1.createTime)
  },
  transfusion (item1, item2) {
    return utils.compareDateTime(item2.handoverTime, item1.handoverTime)
  }
}

const defaultModelComparer = function (item1, item2) {
  return utils.compareDateTime(item2.createTime, item1.createTime)
}

export function getModelComparer (modelName) {
  return modelComparers[modelName] ?? defaultModelComparer
}

export function getMaterialCapacityScale (capacity, unit) {
  switch (capacity) {
    case 200: return MaterialCapacityScale.TwoHundred
    case 300: return MaterialCapacityScale.ThreeHundred
    case 400: return MaterialCapacityScale.FourHundred
    default: return MaterialCapacityScale.Others
  }
}

// 交接班显示，血浆用袋数，其他用U
export function getCountOrCapacityByComponent (row) {
  if (row?.componentName.indexOf('血浆') > 0) {
    return row.count
  } else {
    return row.capacity || row.capacitySum || 0
  }
}
// 交接班库存预警字段名
export function getSingleName (row) {
  if (row?.single <= 0) {
    return '已过期'
  } else {
    return `临期${row?.single}天`
  }
}

function getSemiprodCapacityRange (materialCapacity, semiprodUnit) {
  if (_.isNil(materialCapacity)) materialCapacity = 400
  if (semiprodUnit === BloodUnit.U) { // 红细胞
    return {
      min: 0, max: 2, excludeMin: true, excludeMax: false
    }
  } else { // 血浆
    if (materialCapacity < 300) { // 200mL全血
      return {
        min: 70, max: 150, excludeMin: true, excludeMax: false
      }
    } else if (materialCapacity < 400) { // 300全血
      return {
        min: 70, max: 190, excludeMin: true, excludeMax: false
      }
    } else {
      return {
        min: 70, max: 280, excludeMin: true, excludeMax: false
      }
    }
  }
}

export function getSemiprodCapacityRangeRule (materialCapacity, semiprodUnit) {
  const range = getSemiprodCapacityRange(materialCapacity, semiprodUnit)
  return rules.rangeNumber(range)
}

export function prefectSemiprodCapacity (semiprodCapacity, semiprodUnit, materialCapacity) {
  let grain
  if (semiprodUnit === BloodUnit.U) {
    grain = 0.1
  } else {
    grain = 10
  }
  let result = utils.roundGrain(semiprodCapacity, grain)
  const range = getSemiprodCapacityRange(materialCapacity, semiprodUnit)
  result = utils.clampNumber(result, range)
  return result
}

export function getCollectCategoryFilter ({ collectCategoryIds, collectTypeIdField, collectLocationIdField, collectCategoryWordType }) {
  if (!collectCategoryIds?.length) {
    return function () { return true }
  }
  const collectCategoryMap = store.getters['bis/word/getWordsMapByType'](collectCategoryWordType ?? 'collect_category')
  if (!collectCategoryMap) {
    throw new Error(`获取 ${collectCategoryWordType ?? 'collect_category'} 词条失败`)
  }
  // const collectTypeMap = store.getters['bis/word/getWordsMapByType']('collect_type')
  // if (!collectTypeMap) {
  //   throw new Error('获取 collect_type 词条失败')
  // }
  // const collectLocationMap = store.getters['bis/word/getWordsMapByType']('collect_location')
  // if (!collectLocationMap) {
  //   throw new Error('获取 collect_location 词条失败')
  // }
  const collectCategoryDataList = collectCategoryIds.map(catId => collectCategoryMap.get(catId)?.data)
  return function (item) {
    const collectTypeId = item[collectTypeIdField ?? 'collectTypeId']
    // const collectTypeData = collectTypeMap.get(collectTypeId)?.data
    const collectLocationId = item[collectLocationIdField ?? 'collectLocationId']
    // const collectLocationData = collectLocationMap.get(collectLocationId)?.data
    for (const collectCategoryData of collectCategoryDataList) {
      if (matchCollectCategorySearchFilter({ searchFilter: collectCategoryData.searchFilter, collectTypeId, collectLocationId })) {
        return true
      }
    }
    return false
  }
}

export function getCollectCategorySearchFilter ({ collectCategoryIds, collectCategoryWordType }) {
  if (!collectCategoryIds?.length) {
    return null
  }
  const collectCategoryMap = store.getters['bis/word/getWordsMapByType'](collectCategoryWordType ?? 'collect_category')
  if (!collectCategoryMap) {
    throw new Error(`获取 ${collectCategoryWordType ?? 'collect_category'} 词条失败`)
  }
  const collectCategoryDataList = collectCategoryIds.map(catId => collectCategoryMap.get(catId)?.data)
  return collectCategoryDataList.map(catData => catData.searchFilter).filter(item => item?.length).flat()
}

export function matchCollectCategorySearchFilter ({ searchFilter, collectTypeId, collectLocationId }) {
  console.log(searchFilter)
  console.log(collectTypeId)
  console.log(collectLocationId)
  return searchFilter?.some(item => item.collectTypeIds.includes(collectTypeId) && item.collectLocationIds.includes(collectLocationId))
}

export function matchCollectCategory ({ collectTypeId, collectLocationId, collectCategoryWordType }) {
  const collectCategoryList = store.getters['bis/word/getWordsListByType'](collectCategoryWordType ?? 'collect_category')
  if (!collectCategoryList) {
    throw new Error(`获取 ${collectCategoryWordType ?? 'collect_category'} 词条失败`)
  }
  console.log(collectCategoryList)
  return collectCategoryList?.find(cat => matchCollectCategorySearchFilter({
    searchFilter: cat.data.searchFilter,
    collectLocationId,
    collectTypeId
  }))
}

export function getDisplayTestStateModel (testState) {
  return store.getters['bis/word/getWordsListByType']('display_test_state')?.find(item => item.data.states?.includes(testState))
}

export function getDisplayTestStateText (testState) {
  return getDisplayTestStateModel(testState)?.text ?? ''
}

export function getComponentById (componentId) {
  return store.getters['bis/dic/componentById'](componentId)
}

export function getComponentNameById (componentId) {
  return store.getters['bis/dic/componentNameById'](componentId)
}

export function getProcessNameById (processId) {
  return store.getters['bis/dic/processNameById'](processId)
}

export function getWordTextById (wordId) {
  return store.getters['bis/word/getWordText'](wordId)
}

export function matchProcess ({ processFamilyId, sourceComponentId, targetComponentId, notify }) {
  const process = store.state.bis.dic?.processes?.find(
    item => item.familyId === processFamilyId &&
    item.sourceComponentId === sourceComponentId &&
    (_.isNil(targetComponentId) || item.targetComponentId === targetComponentId))
  if (!process && notify) {
    const targetMsg = _.isNaN(targetComponentId) ? '' : `到 目标成分(${getComponentNameById(targetComponentId)}) `
    Notify.create({ type: 'warning', message: `未匹配到 源成分(${getComponentNameById(sourceComponentId)}) ${targetMsg}的 ${getWordTextById(processFamilyId)} 制备工序` })
  }
  return process
}

export function computeExpireTime ({
  componentId,
  collectTime, productionTime,
  expireStartTime // 传入了expireStartTime就不用传入collectTime, productionTime了
}) {
  const componentModel = getComponentById(componentId)
  if (!componentModel) return null
  if (!expireStartTime) {
    expireStartTime = componentModel.expireStart === ComponentExpireStart.ProductionTime ? productionTime : collectTime
  }
  return utils.formatDateTime(moment(expireStartTime).add(componentModel.expireDays, 'days'))
}
